import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchCyberUsers,
  createCyberUser,
  updateCyberUser,
  deleteCyberUser,
  toggleActiveStatus,
  resetPassword,
  clearError,
} from '../redux/slices/cyberUsersSlice';
import { CyberUser } from '../types';

const CyberUsersManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { cyberUsers, loading, error } = useAppSelector((state) => state.cyberUsers);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CyberUser | null>(null);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    nom: '',
    prenom: '',
    telephone: '',
  });

  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    dispatch(fetchCyberUsers({}));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreateOpen = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      nom: '',
      prenom: '',
      telephone: '',
    });
    setOpenCreateDialog(true);
  };

  const handleCreateClose = () => {
    setOpenCreateDialog(false);
  };

  const handleCreateSubmit = async () => {
    if (!formData.username || !formData.email || !formData.password || !formData.nom || !formData.prenom) {
      return;
    }

    await dispatch(createCyberUser(formData));
    handleCreateClose();
    dispatch(fetchCyberUsers({}));
  };

  const handleEditOpen = (user: CyberUser) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      nom: user.nom,
      prenom: user.prenom,
      telephone: user.telephone || '',
    });
    setOpenEditDialog(true);
  };

  const handleEditClose = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;

    const updates: Partial<CyberUser> = {
      username: formData.username,
      email: formData.email,
      nom: formData.nom,
      prenom: formData.prenom,
      telephone: formData.telephone || undefined,
    };

    await dispatch(updateCyberUser({ id: selectedUser.id, updates }));
    handleEditClose();
    dispatch(fetchCyberUsers({}));
  };

  const handleToggleActive = async (user: CyberUser) => {
    await dispatch(toggleActiveStatus(user.id));
    dispatch(fetchCyberUsers({}));
  };

  const handleResetPasswordOpen = (user: CyberUser) => {
    setSelectedUser(user);
    setNewPassword('');
    setOpenResetPasswordDialog(true);
  };

  const handleResetPasswordClose = () => {
    setOpenResetPasswordDialog(false);
    setSelectedUser(null);
    setNewPassword('');
  };

  const handleResetPasswordSubmit = async () => {
    if (!selectedUser || !newPassword) return;

    await dispatch(resetPassword({ id: selectedUser.id, new_password: newPassword }));
    handleResetPasswordClose();
  };

  const handleDeleteOpen = (user: CyberUser) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    await dispatch(deleteCyberUser(selectedUser.id));
    handleDeleteClose();
    dispatch(fetchCyberUsers({}));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const totalStats = {
    total: cyberUsers.length,
    active: cyberUsers.filter((u: CyberUser) => u.is_active).length,
    inactive: cyberUsers.filter((u: CyberUser) => !u.is_active).length,
    totalClients: cyberUsers.reduce((sum: number, u: CyberUser) => sum + (u.nb_clients || 0), 0),
    totalDemarches: cyberUsers.reduce((sum: number, u: CyberUser) => sum + (u.nb_demarches || 0), 0),
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Gestion des Utilisateurs Cyber
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateOpen}
        >
          Créer un compte Cyber
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Comptes
              </Typography>
              <Typography variant="h4">{totalStats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Actifs
              </Typography>
              <Typography variant="h4" color="success.main">
                {totalStats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Inactifs
              </Typography>
              <Typography variant="h4" color="error.main">
                {totalStats.inactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Clients
              </Typography>
              <Typography variant="h4">{totalStats.totalClients}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Démarches
              </Typography>
              <Typography variant="h4">{totalStats.totalDemarches}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table des utilisateurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Statut</TableCell>
              <TableCell>Nom d'utilisateur</TableCell>
              <TableCell>Nom complet</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell align="center">Clients</TableCell>
              <TableCell align="center">Démarches</TableCell>
              <TableCell>Date de création</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cyberUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary">
                    Aucun utilisateur cyber trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              cyberUsers.map((user: CyberUser) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Actif' : 'Inactif'}
                      color={user.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    {user.nom} {user.prenom}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.telephone || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<PersonIcon />}
                      label={user.nb_clients || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<AssignmentIcon />}
                      label={user.nb_demarches || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditOpen(user)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.is_active ? 'Désactiver' : 'Activer'}>
                        <IconButton
                          size="small"
                          color={user.is_active ? 'warning' : 'success'}
                          onClick={() => handleToggleActive(user)}
                        >
                          {user.is_active ? (
                            <LockIcon fontSize="small" />
                          ) : (
                            <LockOpenIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Réinitialiser le mot de passe">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleResetPasswordOpen(user)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteOpen(user)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de création */}
      <Dialog open={openCreateDialog} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Créer un compte Cyber</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nom d'utilisateur"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Mot de passe"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Nom"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Prénom"
              name="prenom"
              value={formData.prenom}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Téléphone"
              name="telephone"
              value={formData.telephone}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose}>Annuler</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={loading}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de modification */}
      <Dialog open={openEditDialog} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le compte Cyber</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nom d'utilisateur"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Nom"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Prénom"
              name="prenom"
              value={formData.prenom}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Téléphone"
              name="telephone"
              value={formData.telephone}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Annuler</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={loading}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de réinitialisation de mot de passe */}
      <Dialog
        open={openResetPasswordDialog}
        onClose={handleResetPasswordClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Réinitialisation du mot de passe pour{' '}
              <strong>
                {selectedUser?.nom} {selectedUser?.prenom}
              </strong>
            </Alert>
            <TextField
              label="Nouveau mot de passe"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPasswordClose}>Annuler</Button>
          <Button
            onClick={handleResetPasswordSubmit}
            variant="contained"
            color="warning"
            disabled={loading || !newPassword}
          >
            Réinitialiser
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleDeleteClose}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Êtes-vous sûr de vouloir supprimer le compte de{' '}
            <strong>
              {selectedUser?.nom} {selectedUser?.prenom}
            </strong>{' '}
            ? Cette action est irréversible.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Annuler</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CyberUsersManagement;
